import { AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/shared/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useDepartment } from "@/features/departments/hooks/useDepartmentsQueries";
import {
  useAssignManager,
  useUpdateDepartmentSettings,
} from "@/features/departments/hooks/useDepartmentsMutations";
import { useSubAdminCandidates } from "@/features/departments/hooks/useSubAdminCandidates";
import { EditDepartmentDialog } from "@/features/departments/components/EditDepartmentDialog";
import { DepartmentOwnedPagesPanel } from "@/features/departments/components/DepartmentOwnedPagesPanel";
import { useDepartmentPermissions } from "@/hooks/useDepartmentPermissions";
import { useObsReports } from "@/features/obs/hooks/useObsQueries";

interface DepartmentDetailPanelProps {
  departmentId: string;
}

export function DepartmentDetailPanel({ departmentId }: DepartmentDetailPanelProps) {
  const { canManage } = useDepartmentPermissions();
  const { data: department, isLoading } = useDepartment(departmentId);
  const updateSettings = useUpdateDepartmentSettings();
  const assignManager = useAssignManager();

  const { candidates: subAdmins, getUserLabel } = useSubAdminCandidates(
    department?.subAdminId,
  );
  const obsReports = useObsReports();

  /*
   * 🔑 EVERY FLIP SENDS THE WHOLE LIST, not the one report that moved. The server takes a set, so
   * two switches toggled in quick succession cannot interleave into a state nobody chose — and
   * every switch is disabled while a save is in flight, so `department` can never be stale here.
   * Revoking the last report is then an ordinary empty array rather than its own case.
   */
  const grantedObsSlugs = department?.obsReportSlugs ?? [];
  const grantedObsReports = new Set(grantedObsSlugs);

  const toggleObsReport = (slug: string, checked: boolean) => {
    if (!department) return;
    const next = checked
      ? [...grantedObsSlugs, slug]
      : grantedObsSlugs.filter((granted) => granted !== slug);
    updateSettings.mutate({
      id: department.id,
      data: { obsReportSlugs: [...new Set(next)] },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-6" />
      </div>
    );
  }

  if (!department) {
    return (
      <EmptyState
        icon={AlertCircle}
        title="تعذر تحميل بيانات الدائرة"
        description="حدث خطأ أثناء جلب بيانات الدائرة. حاول مرة أخرى."
      />
    );
  }

  return (
    <div className="space-y-6">
      {!canManage && (
        <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground text-right">
          عرض فقط — لا يمكنك تعديل إعدادات الدوائر.
        </div>
      )}

      <div className="flex items-start justify-between gap-3">
        {canManage && <EditDepartmentDialog department={department} />}
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-right">{department.nameAr}</h2>
          <p className="text-sm text-muted-foreground text-right">
            {department.name}
          </p>
          <div className="flex flex-wrap gap-2 mt-2 justify-end">
            <Badge variant="outline" className="font-mono">
              {department.code}
            </Badge>
            <Badge variant={department.isActive ? "default" : "secondary"}>
              {department.isActive ? "نشط" : "غير نشط"}
            </Badge>
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="space-y-4 p-4">
          <h3 className="font-semibold text-right">إعدادات الدائرة</h3>

          <div className="flex w-fit items-center gap-3 rounded-lg border border-border p-3">
            <Label>تفعيل الدائرة</Label>
            <Switch
              checked={department.isActive}
              onCheckedChange={(checked) =>
                updateSettings.mutate({
                  id: department.id,
                  data: { isActive: checked },
                })
              }
              disabled={!canManage || updateSettings.isPending}
            />
          </div>

          <div className="flex w-fit items-center gap-3 rounded-lg border border-border p-3">
            <div>
              <Label>تفعيل سير عمل الموافقة</Label>
              <p className="text-xs text-muted-foreground mt-1">
                عند التفعيل، يتطلب المحتوى مراجعة قبل النشر
              </p>
            </div>
            <Switch
              checked={department.requiresApproval}
              onCheckedChange={(checked) =>
                updateSettings.mutate({
                  id: department.id,
                  data: { requiresApproval: checked },
                })
              }
              disabled={!canManage || updateSettings.isPending}
            />
          </div>

          {/*
            🔄 THIS SWITCH CHANGED MEANING ON 2026-09-12, and the helper text changed with it.

            It used to do one thing: show or hide the «إدارة الأخبار» entry in the CMS nav. That
            entry is a pre-filtered shortcut into «الصفحات», so hiding it removed a doorway and
            restricted nothing — the text said so, deliberately, rather than calling it a
            permission.

            It now ALSO GRANTS: a department switched on lets its CMS users CREATE and DELETE news
            articles, enforced server-side on every call (mof-backend src/utils/news-access.ts),
            even when the dashboard has granted them no content permission at all. So the honest
            description is no longer "a shortcut".

            ⚠️ WHAT IT STILL DOES NOT DO — and the text says this, because it is the part an
            operator will get wrong: it grants NEWS ONLY. An ordinary page is untouched by it, and
            it takes nothing away from anybody who already holds content:create-draft.

            The CMS reads the verdict once per page load, so an editor with the CMS already open
            sees the change on their next RELOAD, not immediately.
          */}
          <div className="flex w-fit items-center gap-3 rounded-lg border border-border p-3">
            <div>
              <Label>تفعيل إدارة الأخبار</Label>
              <p className="text-xs text-muted-foreground mt-1">
                عند التفعيل، يظهر زر «إدارة الأخبار» في نظام إدارة المحتوى
                لمستخدمي هذه الدائرة، ويمكنهم إنشاء الأخبار وحذفها من هناك.
                يقتصر ذلك على الأخبار وحدها — لا يشمل بقية الصفحات، ولا يُنقص
                صلاحيات مَن يملكها أصلًا.
              </p>
            </div>
            <Switch
              checked={department.newsManagementEnabled}
              onCheckedChange={(checked) =>
                updateSettings.mutate({
                  id: department.id,
                  data: { newsManagementEnabled: checked },
                })
              }
              disabled={!canManage || updateSettings.isPending}
            />
          </div>

          {/*
            🔴 UNLIKE THE SWITCH ABOVE IT, THIS ONE GRANTS SOMETHING — AND IT GRANTS ONE REPORT AT
            A TIME. «الموازنة المفتوحة» in the CMS replaces the workbook behind a published budget
            report, and the server re-reads these grants on every call, so removing a report takes
            the capability away rather than merely hiding a row.

            🔄 IT USED TO BE A SINGLE SWITCH FOR THE WHOLE SECTION. That handed a department every
            report there is: the department that maintains the debt bulletin could republish budget
            execution. The list below is the same decision asked once per report.

            A department starts holding NOTHING, including departments that already exist — there
            is no grant until somebody makes one here.

            What a grant does NOT include is creating a report or changing how its columns are
            read: that stays in the wizard, behind obs:manage.
          */}
          <div className="w-fit min-w-72 space-y-3 rounded-lg border border-border p-3">
            <div>
              <Label>تقارير الموازنة المفتوحة</Label>
              <p className="text-xs text-muted-foreground mt-1">
                اختر التقارير التي يمكن لمستخدمي هذه الدائرة فتحها في نظام إدارة المحتوى
                ورفع ملف بيانات جديد لها، أو استعادة إصدار سابق أو حذفه. لا يشمل ذلك إنشاء
                التقارير أو تعديل أعمدتها.
              </p>
            </div>

            {obsReports.isLoading ? (
              <div className="flex justify-center py-2">
                <Spinner className="size-4" />
              </div>
            ) : obsReports.isError ? (
              /*
                A separate permission from the one that opened this panel: the report list is read
                with obs:manage. Saying so beats an empty box that reads as "there are no reports".
              */
              <p className="text-xs text-muted-foreground">
                تعذّر تحميل قائمة التقارير. قد لا تملك صلاحية إدارة تقارير الموازنة المفتوحة.
              </p>
            ) : (obsReports.data ?? []).length === 0 ? (
              <p className="text-xs text-muted-foreground">
                لا توجد تقارير موازنة مفتوحة بعد.
              </p>
            ) : (
              /*
                Rendered in the SERVER'S order, which is the tile order on the public page — so the
                list here reads the same way as the grid the reports appear in.
              */
              <ul className="space-y-2">
                {(obsReports.data ?? []).map((report) => (
                  <li
                    key={report.slug}
                    className="flex items-center justify-between gap-4 text-sm"
                  >
                    <span className="text-right">{report.title.ar || report.slug}</span>
                    <Switch
                      checked={grantedObsReports.has(report.slug)}
                      onCheckedChange={(checked) => toggleObsReport(report.slug, checked)}
                      disabled={!canManage || updateSettings.isPending}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/*
            The delegations twin of the block above, and a SEPARATE decision on purpose. It stays a
            single switch because «الإيفادات الرسمية» is ONE dataset, not a list of reports — there
            is nothing to choose between.
          */}
          <div className="flex w-fit items-center gap-3 rounded-lg border border-border p-3">
            <div>
              <Label>تفعيل الإيفادات الرسمية</Label>
              <p className="text-xs text-muted-foreground mt-1">
                عند التفعيل، يمكن لمستخدمي هذه الدائرة فتح «الإيفادات الرسمية» في نظام
                إدارة المحتوى ورفع ملف بيانات جديد، أو استعادة إصدار سابق أو حذفه.
              </p>
            </div>
            <Switch
              checked={department.delegationsManagementEnabled}
              onCheckedChange={(checked) =>
                updateSettings.mutate({
                  id: department.id,
                  data: { delegationsManagementEnabled: checked },
                })
              }
              disabled={!canManage || updateSettings.isPending}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="space-y-4 p-4">
          <h3 className="font-semibold text-right">تعيين مدير الدائرة</h3>
          <Select
            value={department.subAdminId ?? "NONE"}
            onValueChange={(value) =>
              assignManager.mutate({
                id: department.id,
                data: { subAdminId: value === "NONE" ? null : value },
              })
            }
            disabled={!canManage || assignManager.isPending}
          >
            <SelectTrigger>
              <SelectValue placeholder="اختر مدير الدائرة" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="NONE">بدون مدير</SelectItem>
              {subAdmins.map((user) => (
                <SelectItem key={user.id} value={user.id}>
                  {getUserLabel(user)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <DepartmentOwnedPagesPanel departmentId={department.id} />
    </div>
  );
}
